import {
  Flow,
  Term,
  Tier,
  Selection,
  TermOption,
  PriceEntry,
  PricesList,
  FLOW_TYPES,
} from '../types'
import { FLOW_CONFIG, PREMIUM_CONFIG, TERM_LABELS } from '../constants'
import { trimPrice, money, nounFor } from './format'

export function entryFor(
  flow: Flow,
  tier: Tier,
  term: Term,
  annualPrices: PricesList | null,
  sprintPrices: PricesList | null
): PriceEntry | undefined {
  const types = FLOW_TYPES[flow]
  if (tier === 'premium') return annualPrices?.[types.premium]
  if (term === 'sprint' && FLOW_CONFIG[flow].hasTermToggle) {
    return sprintPrices?.classroompro
  }
  return existingCustomerRate(annualPrices?.[types.pro])
}

/* School Pro: existing schools pay the lower rate, with the quoted price struck through. */
export function existingCustomerRate(
  entry: PriceEntry | undefined
): PriceEntry | undefined {
  if (!entry?.existingCustomerPrice) return entry
  return {
    ...entry,
    price: entry.existingCustomerPrice,
    discountedFrom: entry.price,
  }
}

export function termFor(selection: Selection | null, term: Term): Term {
  if (!selection || selection.tier === 'premium') return 'annual'
  return FLOW_CONFIG[selection.flow].hasTermToggle ? term : 'annual'
}

/** Seats that must be bought so the plan covers everyone already on the account. */
export function isPerSeat(selection: Selection): boolean {
  return FLOW_CONFIG[selection.flow].perSeat && selection.tier !== 'premium'
}

export function seatMinimum(selection: Selection): number {
  return isPerSeat(selection) ? FLOW_CONFIG[selection.flow].minSeats ?? 0 : 0
}

export interface PlanCardVM {
  planName: string
  priceDisplay: string
  // Struck-through price this one replaces, when the plan is discounted.
  originalPriceDisplay?: string
  // e.g. "50% OFF"
  discountLabel?: string
  priceUnit: string
  subtitle?: string
  note?: string
  features: string[]
  recommended: boolean
  showBadge: boolean
  // Small grey corner chip, e.g. "Legacy".
  chip?: string
  termOptions?: TermOption[]
}

export function discountLabelFor(entry: PriceEntry): string | undefined {
  if (!entry.discountedFrom) return undefined
  const from = Number(entry.discountedFrom)
  const to = Number(entry.price)
  if (!from || to >= from) return undefined
  return `${Math.round((1 - to / from) * 100)}% OFF`
}

export function buildProCard(
  flow: Flow,
  entry: PriceEntry,
  term: Term,
  annualPrices: PricesList | null,
  sprintPrices: PricesList | null,
  seatsUsed = 0
): PlanCardVM {
  const cfg = FLOW_CONFIG[flow]
  const noun = nounFor(flow)
  const isSprint = term === 'sprint' && cfg.hasTermToggle
  const termCfg = cfg.terms?.[isSprint ? 'sprint' : 'annual']

  // Annual cross-sells the sprint; sprint carries its own note.
  let note = termCfg?.note
  if (!note && !isSprint && cfg.hasTermToggle && sprintPrices?.classroompro) {
    note = `or try the 12-Week Sprint for $${trimPrice(
      sprintPrices.classroompro.price
    )}`
  }

  const billableSeats = cfg.minSeats ?? 0
  const perSeatTotal = billableSeats * Number(entry.price)
  const discountLabel = discountLabelFor(entry)
  const originalUnit = entry.discountedFrom ? Number(entry.discountedFrom) : 0
  const originalTotal = cfg.perSeat
    ? billableSeats * originalUnit
    : originalUnit

  const features: string[] = []
  if (cfg.perSeat) {
    if (cfg.minSeats) features.push(`Minimum ${cfg.minSeats} ${noun.many}`)
  } else {
    const seatPrice = entry.seatPrice ? Number(entry.seatPrice) : 0
    const suffix =
      cfg.seatBulletShowsExtraPrice && seatPrice
        ? ` · extra seats $${trimPrice(entry.seatPrice!)}/${noun.one} · ${
            isSprint ? '12 weeks' : 'year'
          }`
        : ''
    features.push(`${entry.numSeats} ${noun.many} included${suffix}`)
  }
  features.push(...cfg.features)

  const canToggle =
    cfg.hasTermToggle &&
    annualPrices?.classroompro &&
    sprintPrices?.classroompro

  const subtitle =
    !cfg.perSeat && !cfg.seatBulletShowsExtraPrice
      ? `${entry.numSeats} ${noun.many} included · add more any time`
      : undefined

  return {
    planName: cfg.planName,
    priceDisplay: cfg.perSeat
      ? trimPrice(String(perSeatTotal))
      : trimPrice(entry.price),
    originalPriceDisplay: discountLabel
      ? trimPrice(String(originalTotal))
      : undefined,
    discountLabel,
    priceUnit: termCfg?.priceUnit ?? cfg.priceUnit,
    subtitle,
    note,
    features,
    recommended: true,
    showBadge: cfg.showRecommendedBadge,
    termOptions: canToggle
      ? [
          {
            term: 'annual',
            price: trimPrice(annualPrices!.classroompro!.price),
          },
          {
            term: 'sprint',
            price: trimPrice(sprintPrices!.classroompro!.price),
          },
        ]
      : undefined,
  }
}

/** View model for a flow's legacy Premium card, or null when that flow has no Premium plan. */
export function buildPremiumCard(
  flow: Flow,
  entry: PriceEntry | undefined
): PlanCardVM | null {
  const cfg = PREMIUM_CONFIG[flow]
  if (!cfg || !entry) return null
  const noun = nounFor(flow)
  return {
    planName: cfg.planName,
    priceDisplay: trimPrice(entry.price),
    priceUnit: cfg.priceUnit,
    chip: cfg.chip,
    features: [
      // Per-child plans price every child, so there is no allowance to state.
      ...(cfg.showSeatBullet
        ? [`${entry.numSeats} ${noun.many} included`]
        : []),
      ...cfg.features,
    ],
    recommended: false,
    showBadge: false,
  }
}

export interface PurchaseDetailsVM {
  planName: string
  priceDisplay: string
  // Struck-through unit price and "% OFF" chip, when the plan is discounted.
  originalPriceDisplay?: string
  discountLabel?: string
  includedLabel: string
  includedValue?: number
  extraLabel: string
  extraUnit: string
  seatPrice?: string
  originalSeatPrice?: string
  billingLabel: string
  expiresAt?: number
  total: number
  originalTotal?: number
  perSeat: boolean
}

export function buildPurchaseDetails(
  selection: Selection,
  entry: PriceEntry,
  count: number,
  term: Term,
  seatsUsed = 0
): PurchaseDetailsVM {
  const cfg = FLOW_CONFIG[selection.flow]
  const noun = nounFor(selection.flow)
  const perSeat = isPerSeat(selection)
  const basePrice = Number(entry.price)
  const seatPrice = entry.seatPrice ? Number(entry.seatPrice) : 0
  const isSprint = term === 'sprint'
  const discountLabel = discountLabelFor(entry)
  const originalUnit = entry.discountedFrom ? Number(entry.discountedFrom) : 0

  const originalTotalFor = (): number | undefined => {
    if (!discountLabel) return undefined
    return perSeat ? count * originalUnit : originalUnit + count * seatPrice
  }
  return {
    planName:
      selection.tier === 'premium'
        ? PREMIUM_CONFIG[selection.flow]?.planName ?? cfg.planName
        : `${cfg.planName} ${TERM_LABELS[term]}`,
    priceDisplay: perSeat
      ? `${money(basePrice)} / ${noun.one} · year`
      : `${money(basePrice)} / ${isSprint ? '12 weeks' : 'year'}`,
    originalPriceDisplay: discountLabel ? money(originalUnit) : undefined,
    discountLabel,
    includedLabel: cfg.includedLabel,
    includedValue: perSeat ? undefined : entry.numSeats,
    extraLabel: cfg.extraLabel,
    extraUnit: cfg.extraUnit,
    seatPrice: perSeat ? entry.price : entry.seatPrice,
    // Only a per-seat plan quotes `price` per seat, so only there is discountedFrom a seat price.
    originalSeatPrice:
      discountLabel && perSeat ? entry.discountedFrom : undefined,
    billingLabel: isSprint ? '12 Weeks' : cfg.billingLabel,
    expiresAt: entry.endsAt,
    total: perSeat ? count * basePrice : basePrice + count * seatPrice,
    originalTotal: originalTotalFor(),
    perSeat,
  }
}

export function planNameFor(selection: Selection): string {
  return selection.tier === 'premium'
    ? PREMIUM_CONFIG[selection.flow]?.planName ?? ''
    : FLOW_CONFIG[selection.flow].planName
}
